import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { AlertTriangle, CheckCircle, Calculator as CalculatorIcon } from "lucide-react";
import { openLeadForm } from "@/lib/lead-form";

const TAXAS_REFERENCIA: Record<string, { label: string; taxaMensal: number }> = {
  emprestimo_pessoal: { label: "Emprestimo pessoal", taxaMensal: 6.0 },
  financiamento_imobiliario: { label: "Financiamento imobiliario", taxaMensal: 0.85 },
  financiamento_veicular: { label: "Financiamento veicular", taxaMensal: 1.8 },
  cartao_credito: { label: "Cartao de credito", taxaMensal: 12.0 },
  cheque_especial: { label: "Cheque especial", taxaMensal: 7.5 },
  outro: { label: "Outro", taxaMensal: 4.5 },
};

type CalculationResult = {
  isAbusive: boolean;
  taxaCobrada: number;
  taxaMedia: number;
  percentualAcima: number;
  valorPagoAMais: number;
  tipoContrato: string;
};

const LEAD_FORM_CALCULATOR_EVENT = "leadform:open:calculator";

export const openLeadFormWithCalculatorData = (data: CalculationResult) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(LEAD_FORM_CALCULATOR_EVENT, { detail: data }),
  );
};

export const leadFormCalculatorEventName = LEAD_FORM_CALCULATOR_EVENT;

const formatCurrency = (value: string) => {
  const digits = value.replace(/\D/g, "");
  if (!digits) return "";
  const number = parseInt(digits, 10) / 100;
  return number.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const parseCurrency = (value: string) => {
  const cleaned = value.replace(/\./g, "").replace(",", ".");
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
};

const Calculator = () => {
  const [tipoContrato, setTipoContrato] = useState("");
  const [valorContrato, setValorContrato] = useState("");
  const [taxaJuros, setTaxaJuros] = useState("");
  const [prazoMeses, setPrazoMeses] = useState("");
  const [valorTotalPago, setValorTotalPago] = useState("");
  const [result, setResult] = useState<CalculationResult | null>(null);
  const [hasCalculated, setHasCalculated] = useState(false);

  const handleCurrencyInput = (
    setter: (value: string) => void,
    value: string,
  ) => {
    setter(formatCurrency(value));
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    if (!tipoContrato || !valorContrato || !taxaJuros || !prazoMeses || !valorTotalPago) {
      return;
    }

    const ref = TAXAS_REFERENCIA[tipoContrato];
    if (!ref) return;

    const taxaCobrada = parseFloat(taxaJuros);
    const taxaMedia = ref.taxaMensal;
    const valorOriginal = parseCurrency(valorContrato);
    const totalPago = parseCurrency(valorTotalPago);
    const prazo = parseInt(prazoMeses, 10);

    if (taxaCobrada <= 0 || valorOriginal <= 0 || totalPago <= 0 || prazo <= 0) {
      return;
    }

    const percentualAcima = ((taxaCobrada - taxaMedia) / taxaMedia) * 100;

    const valorJustoTotal =
      valorOriginal * Math.pow(1 + taxaMedia / 100, prazo);
    const valorPagoAMais = Math.max(0, totalPago - valorJustoTotal);

    const isAbusive = percentualAcima > 50;

    const calculationResult: CalculationResult = {
      isAbusive,
      taxaCobrada,
      taxaMedia,
      percentualAcima: Math.round(percentualAcima),
      valorPagoAMais: Math.round(valorPagoAMais * 100) / 100,
      tipoContrato: ref.label,
    };

    setResult(calculationResult);
    setHasCalculated(true);

    if (isAbusive) {
      setTimeout(() => {
        openLeadFormWithCalculatorData(calculationResult);
      }, 1500);
    }
  };

  const handleReset = () => {
    setTipoContrato("");
    setValorContrato("");
    setTaxaJuros("");
    setPrazoMeses("");
    setValorTotalPago("");
    setResult(null);
    setHasCalculated(false);
  };

  return (
    <section id="calculadora" className="py-12 lg:py-20 bg-background scroll-mt-24">
      <div className="container mx-auto px-4">
        <div className="text-center mb-10 lg:mb-16">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-3 lg:mb-4">
            Calculadora de Juros Abusivos
          </h2>
          <p className="text-base lg:text-xl text-muted-foreground max-w-3xl mx-auto px-4">
            Preencha os dados do seu contrato e descubra se voce esta pagando juros acima do permitido
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <Card className="shadow-lg border-accent/20">
            <CardHeader className="text-center">
              <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-3">
                <CalculatorIcon className="w-7 h-7 text-accent" />
              </div>
              <CardTitle className="text-xl lg:text-2xl">Simule agora</CardTitle>
              <CardDescription>
                Informe os dados do seu contrato para verificar se os juros cobrados sao abusivos
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCalculate} className="space-y-5">
                <div>
                  <Label htmlFor="tipoContrato" className="text-foreground font-medium text-sm lg:text-base">
                    Tipo de contrato *
                  </Label>
                  <Select value={tipoContrato} onValueChange={setTipoContrato}>
                    <SelectTrigger className="mt-1.5 h-11 lg:h-12">
                      <SelectValue placeholder="Selecione o tipo de contrato" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(TAXAS_REFERENCIA).map(([key, val]) => (
                        <SelectItem key={key} value={key}>
                          {val.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="valorContrato" className="text-foreground font-medium text-sm lg:text-base">
                    Valor original do contrato (R$) *
                  </Label>
                  <div className="relative mt-1.5">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">R$</span>
                    <Input
                      id="valorContrato"
                      type="text"
                      inputMode="numeric"
                      placeholder="0,00"
                      value={valorContrato}
                      onChange={(e) => handleCurrencyInput(setValorContrato, e.target.value)}
                      className="h-11 lg:h-12 text-base pl-10"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="taxaJuros" className="text-foreground font-medium text-sm lg:text-base">
                    Taxa de juros mensal cobrada (%) *
                  </Label>
                  <div className="relative mt-1.5">
                    <Input
                      id="taxaJuros"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="Ex: 3.5"
                      value={taxaJuros}
                      onChange={(e) => setTaxaJuros(e.target.value)}
                      className="h-11 lg:h-12 text-base pr-10"
                      required
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
                  </div>
                </div>

                <div>
                  <Label htmlFor="prazoMeses" className="text-foreground font-medium text-sm lg:text-base">
                    Prazo do contrato em meses *
                  </Label>
                  <Input
                    id="prazoMeses"
                    type="number"
                    min="1"
                    placeholder="Ex: 48"
                    value={prazoMeses}
                    onChange={(e) => setPrazoMeses(e.target.value)}
                    className="mt-1.5 h-11 lg:h-12 text-base"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="valorTotalPago" className="text-foreground font-medium text-sm lg:text-base">
                    Valor total pago ou a pagar (R$) *
                  </Label>
                  <div className="relative mt-1.5">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">R$</span>
                    <Input
                      id="valorTotalPago"
                      type="text"
                      inputMode="numeric"
                      placeholder="0,00"
                      value={valorTotalPago}
                      onChange={(e) => handleCurrencyInput(setValorTotalPago, e.target.value)}
                      className="h-11 lg:h-12 text-base pl-10"
                      required
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    type="submit"
                    variant="gold"
                    size="lg"
                    className="flex-1 font-semibold text-base"
                  >
                    Calcular
                  </Button>
                  {hasCalculated && (
                    <Button
                      type="button"
                      variant="outline"
                      size="lg"
                      onClick={handleReset}
                    >
                      Limpar
                    </Button>
                  )}
                </div>
              </form>

              {result && (
                <div className="mt-8">
                  {result.isAbusive ? (
                    <div className="rounded-lg border-2 border-destructive/50 bg-destructive/5 p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
                          <AlertTriangle className="w-6 h-6 text-destructive" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-destructive">
                            Juros Possivelmente Abusivos
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            Identificamos irregularidades na taxa cobrada
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="bg-background rounded-md p-3">
                          <p className="text-xs text-muted-foreground">Taxa cobrada</p>
                          <p className="text-lg font-bold text-destructive">{result.taxaCobrada}% a.m.</p>
                        </div>
                        <div className="bg-background rounded-md p-3">
                          <p className="text-xs text-muted-foreground">Taxa media ({result.tipoContrato})</p>
                          <p className="text-lg font-bold text-accent">{result.taxaMedia}% a.m.</p>
                        </div>
                        <div className="bg-background rounded-md p-3">
                          <p className="text-xs text-muted-foreground">Acima da media</p>
                          <p className="text-lg font-bold text-destructive">+{result.percentualAcima}%</p>
                        </div>
                        <div className="bg-background rounded-md p-3">
                          <p className="text-xs text-muted-foreground">Valor pago a mais (est.)</p>
                          <p className="text-lg font-bold text-destructive">
                            R$ {result.valorPagoAMais.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                          </p>
                        </div>
                      </div>

                      <Button
                        variant="gold"
                        size="lg"
                        className="w-full font-semibold"
                        onClick={() => openLeadFormWithCalculatorData(result)}
                      >
                        Solicitar analise gratuita do contrato
                      </Button>
                    </div>
                  ) : (
                    <div className="rounded-lg border-2 border-green-500/30 bg-green-50 p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                          <CheckCircle className="w-6 h-6 text-green-600" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-green-700">
                            Seus juros parecem dentro da normalidade
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            A taxa cobrada esta dentro dos parametros de mercado
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-white rounded-md p-3">
                          <p className="text-xs text-muted-foreground">Taxa cobrada</p>
                          <p className="text-lg font-bold text-foreground">{result.taxaCobrada}% a.m.</p>
                        </div>
                        <div className="bg-white rounded-md p-3">
                          <p className="text-xs text-muted-foreground">Taxa media ({result.tipoContrato})</p>
                          <p className="text-lg font-bold text-accent">{result.taxaMedia}% a.m.</p>
                        </div>
                      </div>

                      <p className="mt-4 text-sm text-muted-foreground">
                        Mesmo assim, se voce tem duvidas sobre seu contrato, nossos advogados podem fazer uma analise detalhada.
                      </p>
                      <Button
                        variant="outline"
                        size="lg"
                        className="w-full mt-3 font-semibold"
                        onClick={() => openLeadForm()}
                      >
                        Falar com um especialista
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Calculator;
